import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import type { AdminUserDetailDto } from '@mafia/core';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useAuth } from '@/store/auth';
import { useGame } from '@/store/game';
import { Avatar } from '@/components/Avatar';
import { Alert, Badge, Button, Card, Field, Input, Modal, PageSpinner } from '@/components/ui';
import { ProfileStats } from '@/components/Stats';
import { EditStatsModal, ResetStatsModal } from './StatsEditor';

const AUDIT_NAMES: Record<string, string> = {
  BAN: 'Блокировка',
  UNBAN: 'Разблокировка',
  GRANT_ADMIN: 'Назначен админом',
  REVOKE_ADMIN: 'Снят с админа',
  DELETE_USER: 'Удаление аккаунта',
  RESET_STATS: 'Статистика обнулена',
  EDIT_STATS: 'Статистика изменена',
};

export function AdminUserDetail() {
  const { id = '' } = useParams();
  const me = useAuth((s) => s.user)!;
  const toast = useGame((s) => s.toast);
  const qc = useQueryClient();
  const user = useQuery({ queryKey: ['admin', 'user', id], queryFn: () => api<AdminUserDetailDto>(`/api/admin/users/${id}`) });
  const navigate = useNavigate();
  const [banOpen, setBanOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState<'edit' | 'reset' | null>(null);
  const [busy, setBusy] = useState(false);

  if (user.isLoading) return <PageSpinner />;
  if (user.isError) return <Alert>Пользователь не найден</Alert>;
  const u = user.data!;
  const self = u.id === me.id;

  async function act(path: string, body?: unknown, ok?: string) {
    setBusy(true);
    try {
      await api(`/api/admin/users/${u.id}/${path}`, { method: 'POST', body });
      if (ok) toast(ok, 'good');
      await qc.invalidateQueries({ queryKey: ['admin'] });
      return true;
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Ошибка', 'bad');
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Все пользователи
      </Link>

      <Card className="p-5">
        <div className="flex flex-wrap items-start gap-4">
          <Avatar id={u.avatarId} name={u.nickname} size={64} />
          <div className="min-w-0 flex-1">
            <h1 className="flex flex-wrap items-center gap-2 font-display text-2xl">
              {u.nickname}
              {u.role === 'ADMIN' && <Badge tone="gold">админ</Badge>}
              {u.isBanned && <Badge tone="red">заблокирован</Badge>}
              {u.online && <Badge tone="green">онлайн</Badge>}
            </h1>
            <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              <Row k="Логин" v={`@${u.login}`} />
              <Row k="Telegram" v={u.telegram ? `@${u.telegram}` : '—'} />
              <Row k="Email" v={u.email} />
              <Row k="ID" v={<span className="font-mono text-xs">{u.id}</span>} />
              <Row k="Регистрация" v={formatDate(u.createdAt)} />
              <Row k="Был в сети" v={u.online ? 'сейчас' : formatDate(u.lastSeenAt)} />
              {u.isBanned && <Row k="Бан до" v={u.bannedUntil ? formatDate(u.bannedUntil) : 'бессрочно'} />}
              {u.isBanned && <Row k="Причина" v={u.banReason ?? '—'} />}
            </dl>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 border-t border-white/5 pt-4">
          {self ? (
            <p className="text-sm text-zinc-500">Это вы — действия над своим аккаунтом недоступны.</p>
          ) : (
            <>
              {u.isBanned ? (
                <Button variant="secondary" loading={busy} onClick={() => act('unban', undefined, 'Пользователь разблокирован')}>
                  Разблокировать
                </Button>
              ) : (
                <Button variant="danger" onClick={() => setBanOpen(true)}>
                  Заблокировать
                </Button>
              )}
              <Button
                variant="ghost"
                loading={busy}
                onClick={() => act('role', { role: u.role === 'ADMIN' ? 'PLAYER' : 'ADMIN' }, u.role === 'ADMIN' ? 'Права администратора сняты' : 'Назначен администратором')}
              >
                {u.role === 'ADMIN' ? 'Снять права админа' : 'Сделать админом'}
              </Button>
              <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                Удалить аккаунт
              </Button>
            </>
          )}
          <Link to={`/admin/games?userId=${u.id}`} className="ml-auto">
            <Button variant="ghost">Матчи игрока</Button>
          </Link>
        </div>
      </Card>

      {!self && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-auto text-sm text-zinc-500">Статистика влияет на профиль и рейтинг.</span>
          <Button variant="secondary" size="sm" onClick={() => setStatsOpen('edit')}>
            Редактировать статистику
          </Button>
          <Button variant="danger" size="sm" onClick={() => setStatsOpen('reset')} disabled={u.profile.totals.played === 0 && u.profile.byRole.length === 0}>
            Обнулить статистику
          </Button>
        </div>
      )}
      <ProfileStats profile={u.profile} />
      <EditStatsModal open={statsOpen === 'edit'} onClose={() => setStatsOpen(null)} userId={u.id} profile={u.profile} />
      <ResetStatsModal open={statsOpen === 'reset'} onClose={() => setStatsOpen(null)} userId={u.id} nickname={u.nickname} />

      <Card className="p-5">
        <h2 className="mb-3 font-display text-lg">История модерации</h2>
        {u.audit.length === 0 ? (
          <p className="text-sm text-zinc-500">Действий не было.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {u.audit.map((a) => (
              <li key={a.id} className="flex flex-wrap gap-x-2">
                <span className="text-zinc-500">{formatDate(a.createdAt)}</span>
                <b>{AUDIT_NAMES[a.action] ?? a.action}</b>
                <span className="text-zinc-400">— {a.actorNickname}</span>
                {a.reason && <span className="text-zinc-400">: «{a.reason}»</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <DeleteModal
        open={deleteOpen}
        nickname={u.nickname}
        onClose={() => setDeleteOpen(false)}
        onDelete={async (reason) => {
          try {
            await api(`/api/admin/users/${u.id}/delete`, { method: 'POST', body: reason ? { reason } : {} });
          } catch (err) {
            toast(err instanceof ApiError ? err.message : 'Ошибка', 'bad');
            return;
          }
          // Сначала уйти со страницы, потом обновлять списки — иначе карточка перезапросится и получит 404.
          navigate('/admin/users', { replace: true });
          qc.removeQueries({ queryKey: ['admin', 'user', u.id] });
          void qc.invalidateQueries({ queryKey: ['admin'] });
          toast('Аккаунт удалён', 'good');
        }}
      />

      <BanModal
        open={banOpen}
        onClose={() => setBanOpen(false)}
        onBan={async (reason, until) => {
          if (await act('ban', { reason, ...(until ? { until } : {}) }, 'Пользователь заблокирован')) setBanOpen(false);
        }}
      />
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="text-zinc-500">{k}:</dt>
      <dd className="min-w-0 truncate">{v}</dd>
    </div>
  );
}

function BanModal({ open, onClose, onBan }: { open: boolean; onClose(): void; onBan(reason: string, until: string | null): Promise<void> }) {
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');
  const [pending, setPending] = useState(false);
  return (
    <Modal open={open} onClose={onClose} title="Блокировка">
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setPending(true);
          await onBan(reason.trim(), until ? new Date(until).toISOString() : null).finally(() => setPending(false));
        }}
      >
        <Field label="Причина">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus maxLength={500} />
        </Field>
        <Field label="До (необязательно)" hint="Пусто — бессрочно. Пользователь будет выведен из игры и разлогинен сразу.">
          <Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" className="w-full" disabled={!reason.trim()} loading={pending}>
          Заблокировать
        </Button>
      </form>
    </Modal>
  );
}

function DeleteModal({ open, nickname, onClose, onDelete }: { open: boolean; nickname: string; onClose(): void; onDelete(reason: string): Promise<void> }) {
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  return (
    <Modal open={open} onClose={onClose} title="Удаление аккаунта">
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setPending(true);
          await onDelete(reason.trim()).finally(() => setPending(false));
        }}
      >
        <p className="text-sm text-zinc-400">
          Аккаунт, сессии и статистика удаляются навсегда. Игрок будет выведен из игры; в истории матчей останется «Удалённый игрок».
        </p>
        <Field label="Причина (необязательно)">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </Field>
        <Field label={`Введите ник «${nickname}» для подтверждения`}>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus autoComplete="off" />
        </Field>
        <Button type="submit" variant="danger" className="w-full" disabled={confirm.trim() !== nickname} loading={pending}>
          Удалить навсегда
        </Button>
      </form>
    </Modal>
  );
}
